<template>
  <div id="importar-raas">
    <Menu />

    <div class="container main">
      <div class="row">
        <div class="col-md-12">
          <SectionTitle
            title="Importar RAAS"
            description="Importar arquivo RAAS do Atendimento Psicossocial"
            url=""
            :enabledModalInformacao="true"
            :enabledIframeModalInformacao="true"
            urlIframeModalInformacao="https://doc.clickup.com/9011510980/d/h/8cj1fp4-1631/599ea88448ce3d9/8cj1fp4-9171"
            pageIdentifier="importar-raas"
          />
        </div>

        <div class="col-md-12">
          <div class="card mb-4">
            <div class="card-body">
              <h5 class="mb-3">Arquivos RAAS</h5>

              <b-alert show variant="warning" class="mb-3">
                <i class="icofont-warning-alt mr-2"></i>

                Lembre-se de importar os três arquivos RAAS caso o município
                possua os três tipos de CAPS.
              </b-alert>

              <b-form-file
                v-model="arquivosRaas"
                browse-text="Selecionar"
                placeholder="Selecione os arquivos RAAS..."
                drop-placeholder="Solte os arquivos aqui..."
                multiple
                @input="previewArquivos"
              ></b-form-file>

              <div v-if="previews.length" class="mt-4">
                <b-alert show variant="info" class="mb-3">
                  <i class="icofont-info-circle mr-2"></i>

                  Esta prévia exibe apenas os primeiros registros de cada
                  arquivo. Todas as regras do layout RAAS serão validadas
                  durante a importação.
                </b-alert>

                <div
                  v-for="(preview, index) in previews"
                  :key="index"
                  class="mb-4"
                >
                  <h6>
                    {{ preview.nome }}
                  </h6>

                  <div class="table-responsive">
                    <table class="table table-sm table-bordered table-preview">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Descrição</th>
                          <th>Conteúdo</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr
                          v-for="(linha, linhaIndex) in preview.linhas"
                          :key="linhaIndex"
                        >
                          <td>{{ linha.tipo }}</td>
                          <td>{{ linha.descricao }}</td>
                          <td class="text-monospace">
                            {{ linha.conteudo }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <button
                class="btn btn-primary mt-3"
                :disabled="isLoading || !arquivosRaas.length"
                @click="importarRaas"
              >
                <template v-if="isLoading">
                  <b-spinner small class="mr-2"></b-spinner>
                  Importando RAAS...
                </template>

                <template v-else> Importar RAAS </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import Menu from '@/components/Menu'
import Footer from '@/components/Footer'
import SectionTitle from '@/components/SectionTitle'

export default {
  name: 'ImportarRaas',

  components: {
    Menu,
    Footer,
    SectionTitle,
  },

  data() {
    return {
      arquivosRaas: [],
      previews: [],
      isLoading: false,
    }
  },

  methods: {
    /**
     * Gera uma prévia simples dos arquivos RAAS.
     */
    async previewArquivos() {
      this.previews = []

      if (!this.arquivosRaas.length) return

      try {
        const previews = await Promise.all(
          this.arquivosRaas.map((arquivo) => this.lerArquivo(arquivo))
        )

        this.previews = previews
      } catch (error) {
        console.error(error)

        this.arquivosRaas = []
        this.previews = []

        this.$toast?.error?.(error.message || 'Erro ao ler os arquivos RAAS.')
      }
    },

    /**
     * Lê um arquivo RAAS e gera sua prévia.
     *
     * @param {File} arquivo
     * @returns {Promise<Object>}
     */
    lerArquivo(arquivo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (event) => {
          const conteudo = String(event.target.result || '')

          const linhas = conteudo
            .split(/\r?\n/)
            .filter((linha) => linha.trim() !== '')

          const primeiraLinha = linhas[0] || ''

          if (!primeiraLinha.startsWith('01')) {
            reject(
              new Error(
                `Arquivo inválido: ${arquivo.name}. O arquivo RAAS deve iniciar pelo registro 01.`
              )
            )

            return
          }

          resolve({
            nome: arquivo.name,

            linhas: linhas.slice(0, 5).map((linha) => {
              const tipo = linha.substring(0, 2)

              return {
                tipo,
                descricao: this.descricaoTipoRegistro(tipo),
                conteudo: linha,
              }
            }),
          })
        }

        reader.onerror = () => {
          reject(new Error(`Erro ao ler o arquivo ${arquivo.name}.`))
        }

        reader.readAsText(arquivo)
      })
    },

    /**
     * Retorna a descrição do tipo de registro RAAS.
     *
     * @param {String} tipo
     * @returns {String}
     */
    descricaoTipoRegistro(tipo) {
      const tipos = {
        '01': 'Cabeçalho',
        15: 'Paciente / Atendimento Psicossocial',
        16: 'Ação / Procedimento',
      }

      return tipos[tipo] || 'Registro desconhecido'
    },

    /**
     * Envia os arquivos RAAS para processamento.
     */
    async importarRaas() {
      if (!this.arquivosRaas.length) {
        this.$toast?.error?.('Selecione pelo menos um arquivo RAAS.')

        return
      }

      const formData = new FormData()

      formData.append('co_ibge', this.$store.state.cod_ibge)
      formData.append('quem_atualizou', this.$store.state.username)
      formData.append('tipo_atualizacao', 'Manual')

      this.arquivosRaas.forEach((arquivo) => {
        formData.append('raas[]', arquivo)
      })

      this.isLoading = true

      try {
        const { data } = await this.$api.post('/app/raas/importar', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        this.$toast?.success?.(
          data.message || 'Arquivos RAAS importados com sucesso.'
        )

        this.arquivosRaas = []
        this.previews = []
      } catch (error) {
        console.error(error)

        this.$toast?.error?.(
          error.response?.data?.message || 'Erro ao importar os arquivos RAAS.'
        )
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#importar-raas {
  .table-responsive {
    display: inherit;
    margin-bottom: 0;
  }

  .text-monospace {
    font-family: monospace;
    word-break: break-all;
  }
}
</style>
